// Vulnerability.jsx — vulnerability tier categorization across scenarios
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
  Chip, Button, ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import { ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import KpiCard from "../components/KpiCard";
import { vulnerabilityApi } from "../api/extra";
import { riskApi } from "../api/api";
import { fmtINR, fmtScore, fmtPct, fmtNum, HAZARD_LABELS } from "../utils/format";

const SCENS = ["current", "2030", "2050"];

export default function Vulnerability() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await vulnerabilityApi.categorize();
      setData(data.vulnerability);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const rescoreAll = async () => {
    setBusy(true);
    try {
      for (const sc of SCENS) await riskApi.rescorePortfolio(sc);
      toast.success("Portfolio re-scored across all scenarios");
      load();
    } catch { toast.error("Re-score failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <DashboardLayout title="Vulnerability"><Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={26} /></Box></DashboardLayout>;
  if (!data) return null;

  return (
    <DashboardLayout
      title="Vulnerability Categorization"
      subtitle="ASSET-CLASSED PHYSICAL-RISK TIERS · T1 → T5"
      headerExtra={
        <Button size="small" variant="contained" onClick={rescoreAll} disabled={busy}
          startIcon={busy ? <CircularProgress size={12} sx={{ color: "#FFF" }} /> : <RefreshCw size={13} />}
          data-testid="vuln-rescore-btn"
          sx={{ background: "#2E5A35", "&:hover": { background: "#1f4023" } }}>
          {busy ? "Computing…" : "Re-score 3 scenarios"}
        </Button>
      }
    >
      {/* Headline: % portfolio vulnerable current vs 2050 */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.6, mb: 2 }}>
        {SCENS.map((sc) => {
          const s = data.by_scenario[sc];
          return (
            <Paper key={sc} sx={{ p: 2.4 }} data-testid={`vuln-card-${sc}`}>
              <Box className="cb-kpi-label">{sc.toUpperCase()} · vulnerable share</Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.4 }}>
                <Box className="cb-display" sx={{ fontSize: 38, fontWeight: 600, color: "#A93226" }}>
                  {fmtPct(s.vulnerable_value_pct)}
                </Box>
                <Box className="cb-mono" sx={{ fontSize: 12, color: "#6B7280" }}>by value</Box>
              </Box>
              <Box sx={{ mt: 1.6, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8 }}>
                <Box>
                  <Box className="cb-kpi-label">Vulnerable assets</Box>
                  <Box className="cb-mono" sx={{ fontSize: 15, fontWeight: 600 }}>{fmtNum(s.vulnerable_count)}</Box>
                </Box>
                <Box>
                  <Box className="cb-kpi-label">Value at risk</Box>
                  <Box className="cb-mono" sx={{ fontSize: 15, fontWeight: 600 }}>{fmtINR(s.vulnerable_value_inr)}</Box>
                </Box>
                <Box>
                  <Box className="cb-kpi-label">Net exposure</Box>
                  <Box className="cb-mono" sx={{ fontSize: 15, fontWeight: 600, color: "#8F5E14" }}>{fmtINR(s.vulnerable_exposure_inr)}</Box>
                </Box>
                <Box>
                  <Box className="cb-kpi-label">Total portfolio value</Box>
                  <Box className="cb-mono" sx={{ fontSize: 12, color: "#6B7280" }}>{fmtINR(data.total_collateral_value_inr)}</Box>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Tier breakdown — one panel per scenario */}
      {SCENS.map((sc) => {
        const s = data.by_scenario[sc];
        return (
          <Paper key={sc} sx={{ p: 0, mb: 2, overflow: "hidden" }} data-testid={`vuln-table-${sc}`}>
            <Box sx={{ p: 2, borderBottom: "1px solid #F0EFEA", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <Box>
                <Box className="cb-display" sx={{ fontSize: 17, fontWeight: 600, color: "#111827" }}>
                  Tier Distribution · {sc.toUpperCase()}
                </Box>
                <Box sx={{ fontSize: 12, color: "#6B7280" }}>
                  {s.unscored.count > 0 ? `${s.unscored.count} asset(s) unscored — they will be excluded.` : "All assets scored."}
                </Box>
              </Box>
              <Chip size="small" label={`${fmtPct(s.vulnerable_value_pct)} vulnerable`} sx={{ background: "#FCE8E6", color: "#A93226", fontWeight: 600 }} />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tier</TableCell>
                  <TableCell>Range</TableCell>
                  <TableCell align="right">Assets</TableCell>
                  <TableCell align="right">% by value</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">Net exposure</TableCell>
                  <TableCell align="right">% of exposure</TableCell>
                  <TableCell>Distribution</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {s.tiers.map((t) => (
                  <TableRow key={t.code} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, background: t.color, borderRadius: 0.4 }} />
                        <Box sx={{ fontWeight: 600 }}>{t.label}</Box>
                      </Box>
                      <Box className="cb-mono" sx={{ fontSize: 10.5, color: "#6B7280", mt: 0.2 }}>{t.code}</Box>
                    </TableCell>
                    <TableCell className="cb-mono" sx={{ fontSize: 12 }}>{t.lo_score}–{t.hi_score}</TableCell>
                    <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{t.count}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtPct(t.pct_value)}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtINR(t.value_inr)}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtINR(t.exposure_inr)}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtPct(t.pct_exposure)}</TableCell>
                    <TableCell>
                      <Box sx={{ background: "#F0EFEA", borderRadius: 1, height: 8, overflow: "hidden", width: 140 }}>
                        <Box sx={{ width: `${Math.min(100, t.pct_value)}%`, height: "100%", background: t.color, transition: "width 0.5s ease" }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {s.unscored.count > 0 && (
                  <TableRow sx={{ background: "#FBFBFA" }}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, background: s.unscored.color, borderRadius: 0.4 }} />
                        <Box sx={{ fontWeight: 600 }}>{s.unscored.label}</Box>
                      </Box>
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell align="right" className="cb-mono">{s.unscored.count}</TableCell>
                    <TableCell colSpan={5} sx={{ fontSize: 11.5, color: "#6B7280" }}>Run "Re-score 3 scenarios" to classify.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        );
      })}

      {/* Top vulnerable assets — uses 2050 view */}
      <Paper sx={{ overflow: "hidden" }} data-testid="vuln-top-assets">
        <Box sx={{ p: 2, borderBottom: "1px solid #F0EFEA" }}>
          <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
            <ShieldAlert size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            Most Vulnerable Assets · 2050 horizon
          </Box>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Collateral</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Top hazard</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell align="right">Exposure</TableCell>
              <TableCell align="right">Score</TableCell>
              <TableCell align="right">Tier</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const tiers = data.by_scenario["2050"]?.tiers || [];
              const rows = [];
              for (const t of tiers) {
                for (const c of (t.collaterals || []).slice(0, 30)) {
                  rows.push({ ...c, tier_label: t.label, tier_color: t.color });
                }
                if (rows.length >= 20) break;
              }
              return rows.slice(0, 20).map((r) => (
                <TableRow key={r.collateral_id} hover
                  onClick={() => navigate(`/collaterals/${r.collateral_id}`)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`vuln-asset-${r.collateral_id}`}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.city || ""} · {r.state || ""}</TableCell>
                  <TableCell>
                    {r.top_hazard ? <span className="hazard-chip">{HAZARD_LABELS[r.top_hazard] || r.top_hazard}</span> : "—"}
                  </TableCell>
                  <TableCell align="right" className="cb-mono">{fmtINR(r.current_valuation_inr)}</TableCell>
                  <TableCell align="right" className="cb-mono">{fmtINR(r.financial_exposure_inr)}</TableCell>
                  <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{fmtScore(r.overall_score)}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={r.tier_label}
                      sx={{ fontFamily: "IBM Plex Mono, monospace", fontSize: 10, background: r.tier_color, color: "#FFFFFF", fontWeight: 600 }} />
                  </TableCell>
                </TableRow>
              ));
            })()}
          </TableBody>
        </Table>
      </Paper>
    </DashboardLayout>
  );
}
